import {CustomError} from "../../shared/errors/CustomErrors";
import {Option} from "../../shared/utilities/OptionT";
import {STRIPE_API_PREFIX} from "../../common/constants";
import {
    ConfirmPaymentSuccessRequest,
    PaymentRequest,
    PaymentResponse,
    RefundRequest,
    RefundResponse,
    PaymentListResponse,
    PaymentIntent
} from "../../shared/entities/Stripe/Stripe";
import {APIDao, Requester} from "../APIRequester";

/**
 * StripeRepository.
 *
 * Responsible for fetching data from Stripe backend
 * by calling APIDao and handling various data
 * coming from API
 */
export class StripeRepository {
    private dao: Requester

    constructor(dao: Requester) {
        this.dao = dao
    }

    /**
     * Create a payment intent
     */
    /**
     * Подтвердить успешную оплату в БД (fallback при отсутствии webhook).
     */
    public async confirmPaymentSuccess(data: ConfirmPaymentSuccessRequest): Promise<Option<PaymentResponse>> {
        return await this.dao.postRequest<PaymentResponse>(`${STRIPE_API_PREFIX}/confirm-payment-success`, {data})
            .then(res => {
                const result: Option<PaymentResponse> = {
                    Some: res.data,
                }
                return result
            }).catch((err: CustomError) => {
                const result: Option<PaymentResponse> = {
                    Some: {
                        success: false,
                        message: err.getErrorDescription()
                    },
                    None: err
                }
                return result
            })
    }

    public async createPaymentIntent(data: PaymentRequest): Promise<Option<PaymentResponse>> {
        return await this.dao.postRequest<PaymentResponse>(`${STRIPE_API_PREFIX}/create-payment-intent`, {data})
            .then(res => {
                const result: Option<PaymentResponse> = {
                    Some: res.data,
                }
                return result
            }).catch((err: CustomError) => {
                const result: Option<PaymentResponse> = {
                    Some: {
                        success: false,
                        message: err.getErrorDescription()
                    },
                    None: err
                }
                return result
            })
    }

    /**
     * Get payment intent by ID
     */
    public async getPaymentIntent(id: string): Promise<Option<PaymentIntent>> {
        return await this.dao.getRequest<PaymentIntent>(`${STRIPE_API_PREFIX}/payment-intent/${id}`, {})
            .then(res => {
                const result: Option<PaymentIntent> = {
                    Some: res.data,
                }
                return result
            }).catch((err: CustomError) => {
                const result: Option<PaymentIntent> = {
                    Some: {} as PaymentIntent,
                    None: err
                }
                return result
            })
    }

    /**
     * Create a refund
     */
    public async createRefund(data: RefundRequest): Promise<Option<RefundResponse>> {
        return await this.dao.postRequest<RefundResponse>(`${STRIPE_API_PREFIX}/refund`, {data})
            .then(res => {
                const result: Option<RefundResponse> = {
                    Some: res.data,
                }
                return result
            }).catch((err: CustomError) => {
                const result: Option<RefundResponse> = {
                    Some: {
                        success: false,
                        message: err.getErrorDescription()
                    },
                    None: err
                }
                return result
            })
    }

    /**
     * List payments with pagination
     */
    public async listPayments(limit: number = 10, startingAfter?: string, endingBefore?: string): Promise<Option<PaymentListResponse>> {
        const params: any = {limit};
        if (startingAfter) params.startingAfter = startingAfter;
        if (endingBefore) params.endingBefore = endingBefore;

        return await this.dao.getRequest<PaymentListResponse>(`${STRIPE_API_PREFIX}/payments`, {params})
            .then(res => {
                const result: Option<PaymentListResponse> = {
                    Some: res.data,
                }
                return result
            }).catch((err: CustomError) => {
                const result: Option<PaymentListResponse> = {
                    Some: {
                        data: [],
                        hasMore: false
                    },
                    None: err
                }
                return result
            })
    }

    /**
     * Get refunds for a specific payment
     */
    public async getRefundsForPayment(paymentIntentId: string): Promise<Option<RefundResponse[]>> {
        return await this.dao.getRequest<RefundResponse[]>(`${STRIPE_API_PREFIX}/payments/${paymentIntentId}/refunds`, {})
            .then(res => {
                const result: Option<RefundResponse[]> = {
                    Some: res.data,
                }
                return result
            }).catch((err: CustomError) => {
                const result: Option<RefundResponse[]> = {
                    Some: [],
                    None: err
                }
                return result
            })
    }

    /**
     * Search payments by email
     */
    public async searchPaymentsByEmail(email: string, limit: number = 50): Promise<Option<PaymentListResponse>> {
        return await this.dao.getRequest<PaymentListResponse>(`${STRIPE_API_PREFIX}/payments/search`, {
            params: {email, limit}
        }).then(res => {
            const result: Option<PaymentListResponse> = {
                Some: res.data,
            }
            return result
        }).catch((err: CustomError) => {
            const result: Option<PaymentListResponse> = {
                Some: {
                    data: [],
                    hasMore: false
                },
                None: err
            }
            return result
        })
    }
}

// Singleton instance
export const StripeRepositoryInstance = new StripeRepository(APIDao);